import { col } from './table.js';
/**
 * The demand layers of the ribbon (PRD 10.1, Design Review V-1; Phase 20), shared by the server
 * read model and the browser client so the two cannot disagree on what a layer is called, which
 * allocation rows it holds, or how it is drawn.
 *
 * A layer is a partition of `forecast_allocations.source_mode`: every mode the database allows
 * belongs to exactly one layer, so the five layers of a week sum to the week's allocated hours
 * and nothing is counted twice or dropped. Stack order is bottom-up as the design review draws
 * it: the load that is there whatever happens (non-project, recurring) under the committed work,
 * with the pipeline on top where it can be read off against the capacity line.
 *
 * Colour and fill. The layers wear hue slots from the validated project-type palette
 * (`palette.js`); they are a second categorical vocabulary and never share a screen with the
 * type hues, so no slot means two things at once. The *order* was chosen with the data-viz
 * validator, not by eye: in this stack every adjacent pair clears the colour-vision gate in both
 * themes (worst adjacent CVD ΔE 9.8 light / 15.3 dark, normal-vision 17.6 / 18.8; the aqua slot
 * sits at 2.82:1 on white, so the ribbon ships a legend, a tooltip and a table). Non-project is
 * the one neutral slot on purpose: it is the load that is not project work. Fill follows the
 * confidence rule of PRD 7.3: the inferred layer is hatched and the pipeline dotted, so neither
 * can be mistaken for a firm commitment.
 */

/** Every `source_mode` the allocation table allows, in the order the CHECK lists them. */
export const SOURCE_MODES = Object.freeze([
  'explicit',
  'inferred_schedule',
  'inferred_role_mix',
  'recurring_baseline',
  'recurring_seasonal',
  'nonproject_baseline',
  'pipeline',
]);

/** The layers, bottom of the stack first. */
export const LAYERS = Object.freeze([
  Object.freeze({
    key: 'nonproject',
    label: 'Non-project',
    source_modes: Object.freeze(['nonproject_baseline']),
    token: '--type-other',
    fill: 'solid',
    confidence_tier: 'medium',
  }),
  Object.freeze({
    key: 'recurring',
    label: 'Recurring',
    source_modes: Object.freeze(['recurring_baseline', 'recurring_seasonal']),
    token: '--type-2',
    fill: 'solid',
    confidence_tier: 'medium',
  }),
  Object.freeze({
    key: 'committed_explicit',
    label: 'Committed (explicit)',
    source_modes: Object.freeze(['explicit']),
    token: '--type-1',
    fill: 'solid',
    confidence_tier: 'high',
  }),
  Object.freeze({
    key: 'committed_inferred',
    label: 'Committed (inferred)',
    source_modes: Object.freeze(['inferred_schedule', 'inferred_role_mix']),
    token: '--type-3',
    fill: 'hatch',
    confidence_tier: 'medium',
  }),
  Object.freeze({
    key: 'pipeline',
    label: 'Pipeline',
    source_modes: Object.freeze(['pipeline']),
    token: '--type-4',
    fill: 'dots',
    confidence_tier: 'low',
  }),
]);

export const LAYER_KEYS = Object.freeze(LAYERS.map((l) => l.key));

const BY_MODE = new Map(LAYERS.flatMap((l) => l.source_modes.map((m) => [m, l.key])));

/**
 * The layer a source mode belongs to, or null for a mode this vocabulary does not know (which
 * the tests guarantee never happens for a mode the database allows).
 * @param {string} sourceMode
 */
export function layerOf(sourceMode) {
  return BY_MODE.get(sourceMode) ?? null;
}

/**
 * What the pipeline layer is called once the read mode is known: the same rows are weighted or
 * unweighted hours, and `none` leaves the layer in place at zero rather than removing it, so
 * the legend and the table keep their shape across the toggle.
 * @param {'weighted' | 'unweighted' | 'none'} pipelineMode
 */
export function pipelineLayerLabel(pipelineMode) {
  if (pipelineMode === 'none') return 'Pipeline (excluded)';
  return `Pipeline (${pipelineMode === 'unweighted' ? 'unweighted' : 'weighted'})`;
}

/**
 * The layer vocabulary a response carries, with the pipeline label resolved for the read mode.
 * @param {'weighted' | 'unweighted' | 'none'} pipelineMode
 */
export function layerVocabulary(pipelineMode = 'weighted') {
  return LAYERS.map((l) => ({
    key: l.key,
    label: l.key === 'pipeline' ? pipelineLayerLabel(pipelineMode) : l.label,
    source_modes: [...l.source_modes],
    token: l.token,
    fill: l.fill,
    confidence_tier: l.confidence_tier,
  }));
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** A zeroed `{ layer_key: 0 }` map, in stack order. */
export function emptyLayers() {
  return Object.fromEntries(LAYER_KEYS.map((k) => [k, 0]));
}

/**
 * One week's stack against its capacity: the total, the headroom, how much stands above the
 * line, and which layer is the first to cross it (the layer the overload is coloured by in V-1).
 * A week with no capacity has no crossing layer: everything is "over", and saying the
 * non-project layer crossed a zero line would be noise.
 *
 * @param {Record<string, number>} layers  hours per layer key
 * @param {number} capacityHours
 */
export function stackWeek(layers, capacityHours) {
  const cap = Number.isFinite(capacityHours) ? capacityHours : 0;
  let running = 0;
  let overLayer = null;
  for (const key of LAYER_KEYS) {
    const hours = Number(layers?.[key]) || 0;
    const top = running + hours;
    if (overLayer === null && cap > 0 && hours > 0 && top > cap) overLayer = key;
    running = top;
  }
  const demand = round2(running);
  return {
    demand_hours: demand,
    headroom_hours: round2(cap - demand),
    over_hours: round2(Math.max(0, demand - cap)),
    over_layer: overLayer,
  };
}

/**
 * The [from, to] span of each layer in a week's stack, bottom-up, for drawing.
 * @param {Record<string, number>} layers
 */
export function layerSegments(layers) {
  let base = 0;
  return LAYER_KEYS.map((key) => {
    const hours = Number(layers?.[key]) || 0;
    const segment = { key, from: base, to: base + hours, hours };
    base += hours;
    return segment;
  });
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)

/**
 * The ribbon's table: one row per week, a column per demand layer. A specification, not a
 * rendering — the screen decorates it with `withFormats` and the CSV writer reads the same list.
 * @param {{ layers?: {key: string, label: string}[] }} opts
 */
export function ribbonColumns({ layers = [] } = {}) {
  return [
    col.week('week_start', 'Week'),
    ...layers.map((layer) => col.hours(`layer_${layer.key}`, layer.label)),
    col.hours('demand_hours', 'Total demand'),
    col.hours('capacity_hours', 'Available capacity'),
    col.hours('headroom_hours', 'Headroom'),
    col.hours('over_hours', 'Over capacity'),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.count('people', 'People'),
    col.count('projects', 'Projects'),
    col.count('opportunities', 'Deals'),
  ];
}

/** The ribbon's rows: a week, with each layer's hours lifted to a column of its own. */
export function ribbonExportRows(weeks, layers) {
  return (weeks ?? []).map((w) => ({
    ...w,
    ...Object.fromEntries((layers ?? []).map((l) => [`layer_${l.key}`, w.layers?.[l.key]])),
  }));
}
